// /blog ページ — Stage 3B 真实化 + Stage 3C draft create + UI 现代化
//
// 范围(2026-07-06):
// - ページタイトル「ブログ管理」+ パンくず「ホーム / ブログ管理」
// - 5 列表格:タイトル / ステータス / 作成日時 / 更新日時 / 操作
// - タイトル左侧浅蓝色 document icon,加粗,slug 副文字
// - ステータス badge:公開(绿) / 下書き(灰蓝) / 非公開(红灰)
// - 操作按钮按 status × role 规则显隐
// - card 顶部右:「+ 下書き作成」(primary) + 「再読み込み」(secondary)
// - card 底部:浅蓝色 info box + 分页 1/1
// - 複製 / 公開 / 下書きに戻す / 削除 接真实后端 API(2026-07-06)
// - 編集中 ボタン disabled + 処理中文言(多重送信防止)
// - 編集 modal(title + content + 投稿情報 / status 切替禁止)
//   GET /api/blog/posts/{id} 取得 → PATCH 更新 → reloadPosts 同期
//
// 不做(本轮 scope 外,1.0 不实现):
// - 多頁分頁(目前固定 1/1)
// - Gutenberg / 画像 / 分類 / タグ / SEO meta
//
// 1.0 では edit / publish / duplicate / delete は audit 上 source=manual
// で記録される(AI agent からの操作は将来 source=ai_agent)。

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw,
  AlertCircle,
  ExternalLink,
  FileText,
  Plus,
  X,
  Edit,
  Copy,
  CheckCircle2,
  Trash2,
  Info,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Table, TableColumn } from "../../components/ui/Table";
import { ApiError } from "../../api/client";
import { blogApi, type BlogPost, type BlogPostActionResponse, type SyncMeta } from "../../api/blog";
import { formatJstDateTime } from "../../utils/dateTime";
import { useAuth } from "../../auth/AuthProvider";
import { BlogEditModal } from "./BlogEditModal";

const TITLE_MAX = 200;
const CONTENT_MAX = 50000;

// 操作ボタンの visiblity 判定(status × role)
type PostStatus = "publish" | "draft" | "private" | "future" | "pending" | string;

interface ActionFlags {
  edit: boolean;
  duplicate: boolean;
  publish: boolean;
  unpublish: boolean;
  remove: boolean;
  openInWp: boolean;
}

function actionFlagsFor(status: PostStatus, isSystemAdmin: boolean): ActionFlags {
  if (isSystemAdmin) {
    if (status === "draft") {
      return { edit: true, duplicate: true, publish: true, unpublish: false, remove: true, openInWp: true };
    }
    if (status === "publish") {
      return { edit: true, duplicate: true, publish: false, unpublish: true, remove: true, openInWp: true };
    }
    if (status === "private") {
      return { edit: true, duplicate: true, publish: true, unpublish: false, remove: true, openInWp: true };
    }
    return { edit: true, duplicate: true, publish: true, unpublish: false, remove: true, openInWp: true };
  }
  // editor(削除/公開/下書き戻す は不可)
  return { edit: true, duplicate: true, publish: false, unpublish: false, remove: false, openInWp: true };
}

function statusBadgeColor(status: string): "green" | "gray" | "yellow" | "red" {
  if (status === "publish") return "green";
  if (status === "draft") return "gray";
  if (status === "future") return "yellow";
  if (status === "private") return "red";
  return "gray";
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    publish: "公開",
    draft: "下書き",
    future: "予約",
    private: "非公開",
    pending: "レビュー待ち",
  };
  return map[status] ?? status;
}

// 各操作ボタンが現在処理中かどうかを追跡する
type BusyActionKind = "duplicate" | "publish" | "unpublish" | "remove" | null;
interface BusyAction {
  postId: number;
  kind: NonNullable<BusyActionKind>;
}

interface ActionConfirmMessages {
  duplicate: string;
  publish: string;
  unpublish: string;
  remove: string;
}
const CONFIRM_MSGS: ActionConfirmMessages = {
  duplicate: "この記事を複製して下書きとして作成しますか?",
  publish: "この記事を公開します。よろしいですか?",
  unpublish: "この記事を下書きに戻します。よろしいですか?",
  remove: "この記事をゴミ箱に移動します。よろしいですか?",
};
const SUCCESS_MSGS = {
  duplicate: "下書きとして複製しました",
  publish: "記事を公開しました",
  unpublish: "記事を下書きに戻しました",
  remove: "記事をゴミ箱に移動しました",
} as const;

// WP の title/excerpt は {rendered: "<HTML>"} の形式。
// Stage 3B は安全のため純テキスト化(React 自動 escape)。
function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, "").trim();
}

interface DraftFormState {
  open: boolean;
  title: string;
  content: string;
  busy: boolean;
  error: string | null;
}

function emptyDraft(): DraftFormState {
  return { open: false, title: "", content: "", busy: false, error: null };
}

export const BlogPage = () => {
  const { isSystemAdmin } = useAuth();

  const [items, setItems] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncMeta, setSyncMeta] = useState<SyncMeta | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshStarted, setAutoRefreshStarted] = useState(false);
  // 操作中状態(postId + kind 両方一致するボタンのみ disabled + 処理中文言)
  const [actionBusy, setActionBusy] = useState<BusyAction | null>(null);

  // Stage Blog Bulk(2026-09-16):一括操作用チェック
  // - selectedIds:checkbox で選んだ post_id の配列(表示中ページ + 検索语に連動)
  // - bulkBusy:true の間チェックボックス + bulk ボタンを disabled
  // - bulkFlash:処理結果サマリ(成功 N 件 / 失敗 M 件)
  // - 操作が完了したら load() で list を再取得する(= cache invalidation)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFlash, setBulkFlash] = useState<string | null>(null);

  // 2026-07-06:タイトル検索 + フロントエンドページネーション
  // - items は API から取得した全件(再読み込みまで不変)
  // - 検索とページングはフロント側で行う(API 呼び出しなし)
  // - 検索クリア時 / 検索語変更時はページを 1 に戻す
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const PAGE_SIZE = 10;

  const [draft, setDraft] = useState<DraftFormState>(emptyDraft());

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const data = await blogApi.listPosts({
        page: 1,
        per_page: 20,
        status: "any",
        // WP REST API の status=any は trash を含む。ゴミ箱移動した記事が
        // 一覧に赤バッジで居座るため、明示的に除外する(2026-07-07 bug fix)。
        exclude: ["trash"],
      });
      setItems(data.items);
      setTotal(data.total);
      setSyncMeta(data.sync_meta ?? null);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      const code = e instanceof ApiError ? e.code : null;
      setError(msg);
      setErrorCode(code);
      setItems([]);
      setTotal(0);
      setSyncMeta(null);
    } finally {
      setLoaded(true);
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loaded || autoRefreshStarted || !syncMeta?.stale) return;
    setAutoRefreshStarted(true);
    void (async () => {
      setRefreshing(true);
      try {
        await blogApi.refreshPosts(false);
        await load();
      } finally {
        setRefreshing(false);
      }
    })();
  }, [autoRefreshStarted, load, loaded, syncMeta]);

  // 検索クリア時にページを 1 に戻す(空文字へ戻した時のみリセット)
  useEffect(() => {
    if (searchQuery === "") {
      setPage(1);
    }
  }, [searchQuery]);

  // 派生:検索フィルタ → ページング
  // - 検索は大小文字区別なし(stripHtml で HTML タグ除去した title で部分一致)
  // - ページングはフィルタ後の items に基づく
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => stripHtml(p.title).toLowerCase().includes(q));
  }, [items, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  // 現在の page が totalPages を超えていたらクランプ(検索で件数が減った時など)
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const isNoConnection = errorCode === "AUTH_WP_NOT_CONFIGURED";

  // 4 つの行操作ハンドラ(複製 / 公開 / 下書き戻す / 削除)
  // 2026-07-06: optimistic update を追加。成功 → 即座に items を更新(待機不要)、
  // 失敗 → 状態を変更しない + alert で日文 error + error_code 表示。
  async function runAction(
    post: BlogPost,
    kind: NonNullable<BusyActionKind>
  ): Promise<void> {
    if (actionBusy) return; // 二重起動防止(防御)
    if (!window.confirm(CONFIRM_MSGS[kind])) return;
    setActionBusy({ postId: post.id, kind });
    try {
      let result: BlogPostActionResponse;
      if (kind === "duplicate") result = await blogApi.duplicatePost(post.id);
      else if (kind === "publish") result = await blogApi.publishPost(post.id);
      else if (kind === "unpublish") result = await blogApi.unpublishPost(post.id);
      else result = await blogApi.deletePost(post.id);

      // 成功 → API の戻り値で即時反映。旧実装は status だけを書き換えてから
      // cached list を再読込していたため、WordPress 側は更新済みなのに UI が
      // 旧 snapshot へ戻って見えることがあった。
      if (kind === "publish") {
        setItems((prev) =>
          prev.map((p) =>
            p.id === post.id
              ? {
                  ...p,
                  status: result.status || "publish",
                  link: result.link ?? p.link,
                  title: result.title || p.title,
                  modified_gmt: new Date().toISOString(),
                }
              : p
          )
        );
      } else if (kind === "unpublish") {
        setItems((prev) =>
          prev.map((p) =>
            p.id === post.id
              ? {
                  ...p,
                  status: result.status || "draft",
                  link: result.link ?? p.link,
                  title: result.title || p.title,
                  modified_gmt: new Date().toISOString(),
                }
              : p
          )
        );
      } else if (kind === "remove") {
        // ゴミ箱移動:即座にリストから削除
        setItems((prev) => prev.filter((p) => p.id !== post.id));
        setTotal((prev) => Math.max(0, prev - 1));
      } else if (kind === "duplicate") {
        setItems((prev) => [
          {
            id: result.wordpress_post_id,
            title: result.title || `${stripHtml(post.title)} のコピー`,
            slug: "",
            status: result.status || "draft",
            link: result.link ?? null,
            date_gmt: new Date().toISOString(),
            modified_gmt: new Date().toISOString(),
            author: null,
            excerpt: "",
          },
          ...prev,
        ]);
        setTotal((prev) => prev + 1);
      }

      window.alert(SUCCESS_MSGS[kind]);

      // 最終同期:WordPress 側を force refresh してから list を読み直す。
      // これを挟まないと外部表示 cache が古いまま残り、「押したのに変わらない」
      // UX になりやすい。
      setRefreshing(true);
      try {
        await blogApi.refreshPosts(true);
        await load();
      } finally {
        setRefreshing(false);
      }
    } catch (e) {
      // 失敗 → items は触らない(原状態維持)
      const msg = e instanceof ApiError ? e.message : String(e);
      const code = e instanceof ApiError ? e.code : "UNKNOWN";
      window.alert(`操作に失敗しました: ${msg}\n(error_code: ${code})`);
    } finally {
      setActionBusy(null);
    }
  }

  // 編集中(将来実装)ボタンは現状 window.confirm で「今後対応予定」を返す
  // 2026-07-06: 編集 modal 起動。BlogEditModal が GET → 編集 → PATCH を行う。
  // 保存成功時 / modal close 時に onSaved で reloadPosts して一覧の title / updated_at を同期。
  const [editingPostId, setEditingPostId] = useState<number | null>(null);

  // Stage Blog Bulk(2026-09-16):一括 publish / unpublish / delete
  // - 単行 runAction と同じ optimistic update パターン(成功 → 即 items 反映 +
  //   refreshPosts(true) + load())で揃える
  // - delete は confirm(ゴミ箱復元できることを明示)
  // - bulk 完了時に selectedIds を必ず空に戻す(次の操作に残らない)
  async function runBulkAction(
    action: "publish" | "unpublish" | "delete",
  ): Promise<void> {
    if (bulkBusy) return;
    if (actionBusy) return;
    const ids = selectedIds.slice();
    if (ids.length === 0) return;
    if (
      action === "delete" &&
      !window.confirm(
        `${ids.length} 件の記事をゴミ箱へ移動します。よろしいですか?`,
      )
    ) {
      return;
    }
    setBulkBusy(true);
    setBulkFlash(null);

    // optimistic:publish / unpublish は status を即書き換え、
    // delete は items から即除去(total 减少)。
    if (action === "publish") {
      setItems((prev) =>
        prev.map((p) =>
          ids.includes(p.id)
            ? { ...p, status: "publish", modified_gmt: new Date().toISOString() }
            : p,
        ),
      );
    } else if (action === "unpublish") {
      setItems((prev) =>
        prev.map((p) =>
          ids.includes(p.id)
            ? { ...p, status: "draft", modified_gmt: new Date().toISOString() }
            : p,
        ),
      );
    } else {
      setItems((prev) => prev.filter((p) => !ids.includes(p.id)));
      setTotal((prev) => Math.max(0, prev - ids.length));
    }
    setSelectedIds([]);

    try {
      const res = await blogApi.bulkAction(action, ids);
      const failed = res.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        setBulkFlash(`一括 ${actionLabel(action)} 完了: ${res.succeeded} 件成功`);
      } else if (res.succeeded === 0) {
        setBulkFlash(
          `一括 ${actionLabel(action)} 失敗: ${res.failed} 件すべてエラー`,
        );
      } else {
        const firstErr = failed[0].error_message || failed[0].error_code || "unknown";
        setBulkFlash(
          `一括 ${actionLabel(action)} 部分失敗: ${res.succeeded} 件成功 / ${res.failed} 件失敗(先頭エラー: ${firstErr})`,
        );
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      const code = e instanceof ApiError ? e.code : "UNKNOWN";
      window.alert(`一括操作に失敗しました: ${msg}\n(error_code: ${code})`);
    } finally {
      // 単行 runAction と同じ最終同期。WP 側の cache を強制更新 + list 再読込。
      setRefreshing(true);
      try {
        await blogApi.refreshPosts(true);
        await load();
      } finally {
        setRefreshing(false);
        setBulkBusy(false);
      }
    }
  }

  function actionLabel(a: "publish" | "unpublish" | "delete"): string {
    if (a === "publish") return "公開";
    if (a === "unpublish") return "下書き戻す";
    return "削除";
  }
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  function openEditModal(post: BlogPost): void {
    setEditingPostId(post.id);
    setEditModalOpen(true);
  }

  function closeEditModal(): void {
    setEditModalOpen(false);
    // editingPostId は次の open まで保持(animations 等で参照される可能性に備え)
  }

  function handleEditSaved(): void {
    // BlogEditModal 内部で PATCH 成功 → 親の一覧を reload
    load();
  }

  // pagedItems(現在表示中の行)に対して allSelected / selectedCount を計算。
  // - ページ外を選んだ selectedIds は表示に影響しないが bulk 対象には含まれる
  // - pagedItems.length === 0 のときは allSelected = false(全解除状態)
  const allSelected =
    pagedItems.length > 0 &&
    pagedItems.every((p) => selectedIds.includes(p.id));
  const selectedCount = selectedIds.length;

  const columns: TableColumn<BlogPost>[] = [
    // Stage Blog Bulk(2026-09-16):チェックボックス列
    // - ヘッダーで全選択 / 全解除(表示中ページのみ)
    // - 行ごとに toggle(選択済みは背景色変え)
    {
      key: "select",
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          disabled={bulkBusy || pagedItems.length === 0}
          onChange={(e) => {
            if (e.target.checked) {
              // 表示中のページ全件 + 既存の選択をマージ(重複は set で除く)
              const merged = new Set(selectedIds);
              for (const p of pagedItems) merged.add(p.id);
              setSelectedIds(Array.from(merged));
            } else {
              // 表示中のページのみ解除(他のページで選んだものは保持)
              const pageIds = new Set(pagedItems.map((p) => p.id));
              setSelectedIds(selectedIds.filter((id) => !pageIds.has(id)));
            }
          }}
          aria-label="このページをすべて選択"
        />
      ),
      width: 34,
      render: (p) => {
        const checked = selectedIds.includes(p.id);
        return (
          <input
            type="checkbox"
            checked={checked}
            disabled={bulkBusy}
            onChange={(e) => {
              setSelectedIds((prev) =>
                e.target.checked
                  ? prev.includes(p.id)
                    ? prev
                    : [...prev, p.id]
                  : prev.filter((id) => id !== p.id),
              );
            }}
            aria-label={`記事 ${p.id} を選択`}
          />
        );
      },
    },
    {
      key: "title",
      header: "タイトル",
      render: (p) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#eff6ff",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FileText size={16} />
          </div>
          <div style={{ minWidth: 0, maxWidth: 360 }}>
            <div
              style={{
                fontWeight: 600,
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
              title={stripHtml(p.title)}
            >
              {stripHtml(p.title) || "(無題)"}
            </div>
            {/* 2026-07-06:slug / endpoint 表示を削除(customer UX polish) */}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "ステータス",
      width: 110,
      render: (p) => (
        <Badge color={statusBadgeColor(p.status)} dot>
          {statusLabel(p.status)}
        </Badge>
      ),
    },
    {
      key: "date",
      header: "作成日時",
      width: 160,
      render: (p) => (
        <span style={{ color: "#6b7280", fontSize: 13 }}>
          {formatJstDateTime(p.date_gmt)}
        </span>
      ),
    },
    {
      key: "modified",
      header: "更新日時",
      width: 160,
      render: (p) => (
        <span style={{ color: "#6b7280", fontSize: 13 }}>
          {formatJstDateTime(p.modified_gmt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "操作",
      align: "right",
      width: 400,
      render: (p) => {
        const flags = actionFlagsFor(p.status, isSystemAdmin);
        // この行のこの kind が処理中か
        const isThisAction = (k: NonNullable<BusyActionKind>) =>
          actionBusy?.postId === p.id && actionBusy.kind === k;
        const isAnyActionOnRow = actionBusy?.postId === p.id;
        return (
          <div className="blog-row-actions">
            {flags.edit && (
              <button
                type="button"
                className="blog-act blog-act-blue"
                onClick={() => openEditModal(p)}
                disabled={isAnyActionOnRow}
              >
                <Edit size={12} />
                <span>編集</span>
              </button>
            )}
            {flags.duplicate && (
              <button
                type="button"
                className="blog-act blog-act-purple"
                onClick={() => runAction(p, "duplicate")}
                disabled={isThisAction("duplicate") || (isAnyActionOnRow && !isThisAction("duplicate"))}
              >
                <Copy size={12} />
                <span>{isThisAction("duplicate") ? "複製中..." : "複製"}</span>
              </button>
            )}
            {flags.publish && (
              <button
                type="button"
                className="blog-act blog-status-action blog-status-action-publish"
                onClick={() => runAction(p, "publish")}
                disabled={isAnyActionOnRow && !isThisAction("publish")}
              >
                <span>{isThisAction("publish") ? "公開中..." : "公開"}</span>
              </button>
            )}
            {flags.unpublish && (
              <button
                type="button"
                className="blog-act blog-status-action blog-status-action-unpublish"
                onClick={() => runAction(p, "unpublish")}
                disabled={isAnyActionOnRow && !isThisAction("unpublish")}
              >
                <span>{isThisAction("unpublish") ? "処理中..." : "下書き"}</span>
              </button>
            )}
            {flags.remove && (
              <button
                type="button"
                className="blog-act blog-act-red"
                onClick={() => runAction(p, "remove")}
                disabled={isAnyActionOnRow && !isThisAction("remove")}
              >
                <Trash2 size={12} />
                <span>{isThisAction("remove") ? "処理中..." : "削除"}</span>
              </button>
            )}
            {flags.openInWp && p.link && (
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-act blog-act-external"
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        );
      },
    },
  ];

  // --- Stage 3C:下書き作成 ---

  function openDraft() {
    setDraft({ ...emptyDraft(), open: true });
  }

  function closeDraft() {
    if (draft.busy) return;
    setDraft(emptyDraft());
  }

  async function submitDraft() {
    const title = draft.title.trim();
    const content = draft.content;
    if (title.length < 1) {
      setDraft({ ...draft, error: "タイトルを入力してください。" });
      return;
    }
    if (title.length > TITLE_MAX) {
      setDraft({ ...draft, error: `タイトルは ${TITLE_MAX} 文字以内で入力してください。` });
      return;
    }
    if (content.length < 1) {
      setDraft({ ...draft, error: "本文を入力してください。" });
      return;
    }
    if (content.length > CONTENT_MAX) {
      setDraft({ ...draft, error: `本文は ${CONTENT_MAX} 文字以内で入力してください。` });
      return;
    }
    setDraft({ ...draft, busy: true, error: null });
    try {
      await blogApi.createDraft({ title, content });
      window.alert("下書きを作成しました。この一覧から公開できます。");
      setDraft(emptyDraft());
      await blogApi.refreshPosts(true);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setDraft({ ...draft, busy: false, error: msg });
    }
  }

  function Breadcrumb() {
    return (
      <nav
        aria-label="パンくず"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          color: "#6b7280",
          marginBottom: 8,
        }}
      >
        <a href="/dashboard" style={{ color: "#6b7280", textDecoration: "none" }}>
          ホーム
        </a>
        <span style={{ color: "#9ca3af" }}>/</span>
        <span style={{ color: "#111827", fontWeight: 600 }}>ブログ管理</span>
      </nav>
    );
  }

  function BlogInfoBox() {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 14px",
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: 10,
          color: "#1e3a8a",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        <Info size={16} style={{ flexShrink: 0, marginTop: 2, color: "#2563eb" }} />
        <div>
          公開すると、Webサイトに記事が表示されます。AI生成記事もこの画面と AI ブログ生成画面から公開・下書き戻しを管理できます。
        </div>
      </div>
    );
  }

  function Pagination({
    page,
    totalPages,
    onPrev,
    onNext,
  }: {
    page: number;
    totalPages: number;
    onPrev: () => void;
    onNext: () => void;
  }) {
    const isFirst = page <= 1;
    const isLast = page >= totalPages;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderTop: "1px solid #e5e7eb",
          fontSize: 13,
          color: "#6b7280",
        }}
      >
        <div>{page} / {totalPages} ページ</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="blog-page-btn"
            onClick={onPrev}
            disabled={isFirst}
            title={isFirst ? "最初のページです" : "前のページ"}
          >
            <ChevronLeft size={14} />
            <span>前へ</span>
          </button>
          <button
            type="button"
            className="blog-page-btn"
            onClick={onNext}
            disabled={isLast}
            title={isLast ? "最後のページです" : "次のページ"}
          >
            <span>次へ</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div style={{ padding: 24 }}>
        <Breadcrumb />
        <div className="page-title">
          <div className="page-title-text">ブログ管理</div>
        </div>
        <Card>
          <div style={{ textAlign: "center", color: "#6b7280" }}>読み込み中...</div>
        </Card>
      </div>
    );
  }

  if (isNoConnection) {
    return (
      <div style={{ padding: 24 }}>
        <Breadcrumb />
        <div className="page-title">
          <div className="page-title-text">ブログ管理</div>
        </div>
        <Card title="WordPress 接続が未設定です">
          <div
            style={{
              padding: "14px 16px",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 10,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <AlertCircle size={20} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
                WordPress 接続が設定されていません
              </div>
              <div style={{ fontSize: 13, color: "#78350f", marginBottom: 12 }}>
                {error}
              </div>
              <a
                href="/settings"
                style={{
                  display: "inline-block",
                  padding: "8px 14px",
                  background: "#2563eb",
                  color: "#fff",
                  borderRadius: 8,
                  textDecoration: "none",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                /settings ページで接続設定する →
              </a>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Breadcrumb />
        <div className="page-title">
          <div className="page-title-text">ブログ管理</div>
        </div>
        <Card
          title="記事一覧"
          subtitle="WordPress から記事一覧を取得できませんでした"
          actions={
            <Button
              iconLeft={<RefreshCw size={14} />}
              onClick={load}
              disabled={busy}
              variant="secondary"
            >
              再試行
            </Button>
          }
        >
          <div
            style={{
              padding: "12px 14px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
              color: "#b91c1c",
              fontSize: 13,
            }}
          >
            <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {error}
            {errorCode && (
              <span style={{ marginLeft: 8, fontFamily: "Menlo, monospace", fontSize: 12 }}>
                ({errorCode})
              </span>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb />

      <div className="page-title">
        <div className="page-title-text">ブログ管理</div>
      </div>

      {draft.open && (
        <div style={{ marginBottom: 18 }}>
          <Card
            title="下書き作成"
            subtitle="WordPress に下書き(draft)として保存されます。作成後はこの一覧から公開できます。"
            actions={
              <Button
                iconLeft={<X size={14} />}
                onClick={closeDraft}
                disabled={draft.busy}
                variant="ghost"
              >
                閉じる
              </Button>
            }
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="field">
                <label className="field-label">
                  タイトル <span style={{ color: "#dc2626" }}>*</span>
                  <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 8 }}>
                    {draft.title.length} / {TITLE_MAX}
                  </span>
                </label>
                <input
                  className="input"
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="例: 新商品のお知らせ"
                  disabled={draft.busy}
                  maxLength={TITLE_MAX}
                />
              </div>

              <div className="field">
                <label className="field-label">
                  本文 <span style={{ color: "#dc2626" }}>*</span>
                  <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 8 }}>
                    {draft.content.length} / {CONTENT_MAX}
                  </span>
                </label>
                <textarea
                  className="input"
                  value={draft.content}
                  onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  placeholder="本文を入力してください。HTML も使用できます。"
                  disabled={draft.busy}
                  rows={10}
                  style={{
                    resize: "vertical",
                    fontFamily: "ui-monospace, Menlo, monospace",
                    fontSize: 13,
                  }}
                />
                <span className="field-hint">
                  ※ 下書きで作成されます(status は backend 側で固定)。
                </span>
              </div>

              {draft.error && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
                  {draft.error}
                </div>
              )}

              <div className="row gap-8">
                <Button onClick={submitDraft} disabled={draft.busy}>
                  {draft.busy ? "作成中..." : "下書きを作成"}
                </Button>
                <Button variant="ghost" onClick={closeDraft} disabled={draft.busy}>
                  キャンセル
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card
        title="記事一覧"
        subtitle={
          searchQuery.trim()
            ? `検索結果 ${filteredItems.length} 件 / 全 ${total} 件`
            : `全 ${total} 件 / 表示 ${pagedItems.length} 件(WordPress)`
        }
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* 2026-07-06:タイトル検索(search box) */}
            <div className="blog-search">
              <Search size={14} className="blog-search-icon" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const next = e.target.value;
                  setSearchQuery(next);
                  // 検索語変更時はページ 1 に戻す(空文字へ戻した時も同じだが、
                  // 別 useEffect で page=1 同期するのでここでは 1 を強制しない)
                  if (next !== "") setPage(1);
                }}
                placeholder="タイトルで検索"
                disabled={busy}
                aria-label="タイトルで検索"
              />
            </div>
            <Button
              iconLeft={<Plus size={14} />}
              onClick={openDraft}
              disabled={busy || !!actionBusy}
            >
              下書き作成
            </Button>
            <Button
              iconLeft={<RefreshCw size={14} />}
              onClick={() => void (async () => {
                setRefreshing(true);
                try {
                  await blogApi.refreshPosts(true);
                  await load();
                } finally {
                  setRefreshing(false);
                }
              })()}
              disabled={busy || !!actionBusy || refreshing}
              variant="secondary"
            >
              {refreshing ? "更新中..." : "今すぐ更新"}
            </Button>
          </div>
        }
        noPadding
      >
        {/* Stage Blog Bulk(2026-09-16):一括操作 bar
            - chatbot 知識ベースと同じ blue bar 形式
            - selectedCount > 0 の時だけ表示
            - publish / unpublish / delete の 3 アクション + 選択解除
        */}
        {selectedCount > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              padding: "10px 16px",
              borderRadius: 0,
              borderBottom: "1px solid #dbeafe",
              background: "#eff6ff",
              color: "#1e3a8a",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span>{selectedCount} 件選択中</span>
            <Button
              variant="secondary"
              disabled={bulkBusy}
              onClick={() => void runBulkAction("publish")}
            >
              {bulkBusy ? "処理中..." : "公開"}
            </Button>
            <Button
              variant="secondary"
              disabled={bulkBusy}
              onClick={() => void runBulkAction("unpublish")}
            >
              {bulkBusy ? "処理中..." : "下書き"}
            </Button>
            <Button
              variant="danger"
              disabled={bulkBusy}
              onClick={() => void runBulkAction("delete")}
            >
              {bulkBusy ? "処理中..." : "削除"}
            </Button>
            <Button
              variant="ghost"
              disabled={bulkBusy}
              onClick={() => setSelectedIds([])}
            >
              選択解除
            </Button>
          </div>
        )}
        {bulkFlash && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              background: "#f0fdf4",
              borderBottom: "1px solid #bbf7d0",
              color: "#15803d",
              fontSize: 13,
            }}
          >
            <CheckCircle2 size={14} />
            <span>{bulkFlash}</span>
          </div>
        )}
        <Table
          columns={columns}
          data={pagedItems}
          rowKey={(p) => String(p.id)}
          emptyText={searchQuery.trim() ? "検索条件に一致する記事がありません" : "投稿がありません"}
        />
        <Pagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </Card>

      <div style={{ marginTop: 18 }}>
        <BlogInfoBox />
      </div>

      {/* 2026-07-06: 編集 modal(title + content + 投稿情報、status 切替禁止) */}
      <BlogEditModal
        postId={editingPostId}
        open={editModalOpen}
        onClose={closeEditModal}
        onSaved={handleEditSaved}
      />
    </div>
  );
};
